import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaShoppingCart, FaTrash, FaPlus, FaMinus, FaArrowLeft, FaMapMarkerAlt } from 'react-icons/fa';
import { getCart, removeFromCart, updateQuantity, clearCart, getNumericPrice } from '../utils/cartUtils';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import { useToast } from '../components/ToastProvider';
import ConfirmationModal from '../components/ConfirmationModal';
import { getSavedPincode } from '../data/serviceablePincodes';
import { calculateShipping, getSavedShippingInfo, saveShippingInfo, getFreeShippingProgress, FREE_SHIPPING_THRESHOLD } from '../utils/shippingUtils';
import './CartPage.css';

const CartPage = () => {
  const [cartItems, setCartItems] = useState([]);
  // const [showCheckoutForm, setShowCheckoutForm] = useState(false); // Removed
  const [isClearCartModalOpen, setIsClearCartModalOpen] = useState(false);
  const [orderResult, setOrderResult] = useState(null);
  const [pincode, setPincode] = useState('');
  const [shippingCost, setShippingCost] = useState(null);
  const [shippingLocation, setShippingLocation] = useState(null);
  const [isFreeShipping, setIsFreeShipping] = useState(false);
  const { showToast } = useToast();
  const { customer } = useCustomerAuth();
  const navigate = useNavigate();
  const location = useLocation();


  useEffect(() => {
    setCartItems(getCart());

    // DON'T auto-load pincode - let user enter manually for better UX
    // Removed auto-population logic

    // Listen for storage changes (when cart is updated from other tabs/components)
    const handleStorageChange = () => {
      setCartItems(getCart());
    };
    window.addEventListener('storage', handleStorageChange);

    // Custom event for same-tab updates
    window.addEventListener('cartUpdated', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('cartUpdated', handleStorageChange);
    };
  }, []);

  // Auto-detect free shipping eligibility when cart changes
  useEffect(() => {
    const subtotal = calculateTotal();
    const progress = getFreeShippingProgress(subtotal);

    // If eligible for free shipping, auto-set it (no pincode needed for amount calculation)
    if (progress.eligible && shippingCost === null) {
      setShippingCost(0);
      setIsFreeShipping(true);
      setShippingLocation('Gujarat'); // Generic location for free shipping
    }
    // If not eligible anymore (cart reduced), reset if it was showing free
    else if (!progress.eligible && isFreeShipping) {
      setShippingCost(null);
      setIsFreeShipping(false);
      setShippingLocation(null);
    }
  }, [cartItems]); // Re-run when cart items change


  const handleRemove = (cartId) => {
    removeFromCart(cartId);
    setCartItems(getCart());
    window.dispatchEvent(new Event('cartUpdated'));
    showToast('Removed from cart', 'info');
  };

  const handleQuantityChange = (cartId, newQty) => {
    if (newQty < 1) {
      handleRemove(cartId);
      return;
    }
    updateQuantity(cartId, newQty);
    setCartItems(getCart());
    window.dispatchEvent(new Event('cartUpdated'));
  };

  const handlePincodeInputChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setPincode(value);
  };

  const handlePincodeCheck = (pincodeToCheck = pincode) => {
    if (!pincodeToCheck || pincodeToCheck.length !== 6) {
      showToast('Please enter a valid 6-digit pincode', 'error');
      return;
    }

    const subtotal = calculateTotal();
    const result = calculateShipping(pincodeToCheck, subtotal);

    if (result.error) {
      showToast(result.error, 'error');
      setShippingCost(null);
      setShippingLocation(null);
      setIsFreeShipping(false);
    } else {
      setShippingCost(result.amount);
      setShippingLocation(result.location);
      setIsFreeShipping(result.isFree || false);
      saveShippingInfo(pincodeToCheck, result.amount, result.location);

      if (result.isFree) {
        showToast('🎉 You qualify for FREE SHIPPING!', 'success');
      } else {
        showToast(`Shipping: ₹${result.amount}`, 'success');
      }
    }
  };

  const calculateTotal = () => {
    return cartItems.reduce((sum, item) => {
      const price = getNumericPrice(item.price);
      return sum + (item.quantity * price);
    }, 0);
  };

  useEffect(() => {
    // Check for redirect state telling us to open checkout immediately
    if (location.state?.openCheckout && customer && cartItems.length > 0) {
      // Redirect to /checkout directly instead of modal
      navigate('/checkout');
    }
  }, [location, customer, cartItems, navigate]);

  const handleClearCart = () => {
    setIsClearCartModalOpen(true);
  };

  const handleConfirmClearCart = () => {
    clearCart();
    setCartItems([]);
    window.dispatchEvent(new Event('cartUpdated'));
    setIsClearCartModalOpen(false);
    showToast('Cart cleared successfully', 'success');
  };

  const handleCheckout = () => {
    if (cartItems.length === 0) return;

    if (!customer) {
      showToast('Please login to checkout', 'info');
      navigate('/login', { state: { from: '/cart', openCheckout: true } });
      return;
    }

    // Only show shipping notice if pincode NOT entered
    // Don't show if already calculated (including free shipping)
    if (shippingCost === null) {
      // showToast('Shipping will be calculated at checkout based on your address', 'info');
    }

    // Navigate to dedicated Checkout Page
    navigate('/checkout');
  };

  return (
    <div className="cart-page">
      <div className="cart-container">
        <motion.div
          className="cart-header"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Link to="/" className="back-to-home">
            <FaArrowLeft />
            Continue Shopping
          </Link>
          <h1 className="cart-title">
            <FaShoppingCart />
            Your Shopping Cart
          </h1>
        </motion.div>

        {cartItems.length === 0 ? (
          <motion.div
            className="cart-empty"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <div className="empty-cart-icon">
              <FaShoppingCart />
            </div>
            <h2>Your cart is empty</h2>
            <p>Looks like you haven't added anything to your cart yet.</p>
            <Link to="/#products" className="btn btn-primary">
              Browse Products
            </Link>
          </motion.div>
        ) : (
          <>
            <div className="cart-items">
              {cartItems.map((item, index) => {
                const price = getNumericPrice(item.price);
                const itemTotal = item.quantity * price;
                // Use cartId as key (it's unique per variant selection)
                // Fallback to item.id if cartId is missing (legacy items)
                const key = item.cartId || item.id;

                return (
                  <motion.div
                    key={key}
                    className="cart-item"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <div className="cart-item-image">
                      <img src={item.image} alt={item.name} loading="lazy" />
                    </div>
                    <div className="cart-item-details">
                      <h3>
                        {item.name}
                        {item.selectedVariant && (
                          <span className="cart-item-variant"> — {item.selectedVariant.label}</span>
                        )}
                      </h3>
                      <p className="cart-item-price">₹{price} each</p>
                      <div className="cart-item-controls">
                        <div className="quantity-controls">
                          <motion.button
                            className="quantity-btn"
                            onClick={() => handleQuantityChange(key, item.quantity - 1)}
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            disabled={item.quantity === 1}
                          >
                            <FaMinus />
                          </motion.button>
                          <span className="quantity-value">{item.quantity}</span>
                          <motion.button
                            className="quantity-btn"
                            onClick={() => handleQuantityChange(key, item.quantity + 1)}
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                          >
                            <FaPlus />
                          </motion.button>
                        </div>
                        <div className="cart-item-total">
                          <strong>₹{itemTotal}</strong>
                        </div>
                      </div>
                    </div>
                    <motion.button
                      className="remove-btn"
                      onClick={() => handleRemove(key)}
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <FaTrash />
                    </motion.button>
                  </motion.div>
                );
              })}
            </div>

            <motion.div
              className="cart-summary"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              {orderResult && (
                <div className="order-success" style={{
                  background: 'rgba(183, 43, 91, 0.1)',
                  color: '#b72b5b',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  marginBottom: '16px',
                  fontSize: '0.95rem',
                  fontFamily: 'Inter, sans-serif'
                }}>
                  Order placed! ID: {orderResult.order_id || orderResult.id || 'N/A'}. We will contact you soon.
                </div>
              )}
              <div className="summary-row">
                <span>Subtotal ({cartItems.reduce((sum, item) => sum + item.quantity, 0)} items):</span>
                <strong>₹{calculateTotal()}</strong>
              </div>

              {/* Always show free shipping progress when not eligible */}
              {(() => {
                const progress = getFreeShippingProgress(calculateTotal());
                if (!progress.eligible && progress.remaining > 0) {
                  return (
                    <div className="free-shipping-progress-top">
                      <small>💡 Add ₹{progress.remaining} more for FREE SHIPPING!</small>
                    </div>
                  );
                }
                return null;
              })()}

              {/* Mini Pincode Checker */}
              <div className="cart-pincode-section">
                <label className="pincode-label">
                  <FaMapMarkerAlt /> {isFreeShipping ? 'Confirm Pincode (Optional)' : 'Enter Pincode for Shipping Estimate'}
                </label>
                <div className="pincode-input-group">
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    className="pincode-input-mini"
                    placeholder="6-digit pincode"
                    value={pincode}
                    onChange={handlePincodeInputChange}
                    maxLength={6}
                  />
                  <button
                    className="pincode-check-btn-mini"
                    onClick={() => handlePincodeCheck()}
                    disabled={pincode.length < 6}
                  >
                    Check
                  </button>
                </div>
              </div>

              {/* Shipping Cost Display */}
              {shippingCost !== null ? (
                <>
                  {isFreeShipping ? (
                    <div className="free-shipping-banner">
                      <span className="free-shipping-icon">🎉</span>
                      <span className="free-shipping-text">FREE SHIPPING - You saved ₹80-100!</span>
                    </div>
                  ) : (
                    <>
                      <div className="summary-row">
                        <span>Shipping:</span>
                        <strong>₹{shippingCost}</strong>
                      </div>
                      {/* Show progress toward free shipping */}
                      {(() => {
                        const progress = getFreeShippingProgress(calculateTotal());
                        // Always show progress hint when not eligible (removed <= 1000 restriction)
                        if (!progress.eligible && progress.remaining > 0) {
                          return (
                            <div className="free-shipping-progress">
                              <small>💡 Add ₹{progress.remaining} more for FREE SHIPPING!</small>
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </>
                  )}
                  <div className="summary-divider"></div>
                  <div className="summary-row summary-total">
                    <span>Total:</span>
                    <strong>₹{calculateTotal() + shippingCost}</strong>
                  </div>
                </>
              ) : (
                <p className="shipping-notice">Enter pincode to calculate shipping</p>
              )}
              <div className="summary-actions">
                <motion.button
                  className="btn btn-secondary"
                  onClick={handleClearCart}
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Clear Cart
                </motion.button>
                <motion.button
                  className="btn btn-primary btn-checkout"
                  onClick={handleCheckout}
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <span style={{ marginRight: 8 }}>🔒</span>
                  Checkout Now
                </motion.button>
              </div>
            </motion.div>
          </>
        )}
      </div>



      <ConfirmationModal
        isOpen={isClearCartModalOpen}
        onClose={() => setIsClearCartModalOpen(false)}
        onConfirm={handleConfirmClearCart}
        title="Clear Cart?"
        message="This will remove all items from your cart. This action can't be undone."
      />
    </div>
  );
};

export default CartPage;


